import React, { useRef, useState, useEffect } from "react";

const TYPE_META = {
  success: {
    icon: "check_circle",
    bg: "bg-emerald-50",
    iconColor: "text-emerald-500",
    dot: "bg-emerald-400",
  },
  warning: {
    icon: "warning",
    bg: "bg-amber-50",
    iconColor: "text-amber-500",
    dot: "bg-amber-400",
  },
  error: {
    icon: "error",
    bg: "bg-rose-50",
    iconColor: "text-rose-500",
    dot: "bg-rose-400",
  },
  info: {
    icon: "info",
    bg: "bg-sky-50",
    iconColor: "text-sky-500",
    dot: "bg-sky-400",
  },
};

function fmtTime(ts) {
  const d = new Date(ts);
  if (isNaN(d)) return "";
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function NotificationPanel({
  notifications = [],
  unreadCount = 0,
  onMarkRead,
  onMarkAllRead,
  onClearAll,
}) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ✅ Just toggle open — do NOT call onMarkAllRead here
  const handleOpen = () => setOpen((prev) => !prev);

  return (
    <div className="relative" ref={panelRef}>
      {/* ── Bell Button ── */}
      <button
        onClick={handleOpen}
        title="Notifications"
        className="relative w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:text-[#08757c] hover:bg-[#08757c]/8 transition-all duration-150"
      >
        <span className="material-symbols-outlined text-[20px]">
          notifications
        </span>

        {/* Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[9px] font-extrabold leading-none">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}

        {/* Unread indicator dot — only when at least one notification is unread */}
        {unreadCount > 0 && (
          <span className="absolute bottom-0 right-0 w-2 h-2 rounded-full border border-white bg-emerald-400" />
        )}
      </button>

      {/* ── Dropdown Panel ── */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.14)] border border-slate-100 overflow-hidden z-50">
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-br from-[#005b60] to-[#08757c]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <span className="material-symbols-outlined text-[18px] text-white flex-shrink-0">
                  notifications
                </span>
                <span className="text-sm font-bold text-white truncate">
                  Notifications
                </span>
              </div>
              <button
                onClick={() => setOpen(false)}
                title="Close"
                className="w-6 h-6 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
              >
                <span className="material-symbols-outlined text-[16px]">
                  close
                </span>
              </button>
            </div>

            {unreadCount > 0 && (
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] font-extrabold bg-white/20 text-white px-2 py-0.5 rounded-full">
                  {unreadCount} new
                </span>
                {/* ✅ Mark all read only on explicit button click */}
                <button
                  onClick={onMarkAllRead}
                  className="text-white/70 hover:text-white text-[10px] font-semibold transition-colors whitespace-nowrap"
                >
                  Mark all read
                </button>
              </div>
            )}
          </div>

          {/* ── List ── */}
          <div className="overflow-y-auto max-h-72">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 gap-2">
                <span className="material-symbols-outlined text-[36px] text-slate-200">
                  notifications_off
                </span>
                <p className="text-xs text-slate-400 font-medium">
                  No notifications yet
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((n) => {
                  const meta = TYPE_META[n.type] ?? TYPE_META.info;
                  return (
                    <div
                      key={n.id}
                      onClick={() => onMarkRead?.(n.id)}
                      className={`relative flex gap-3 px-4 py-3 cursor-pointer transition-colors ${
                        n.read
                          ? "bg-white hover:bg-slate-50"
                          : "bg-[#edf6ff] hover:bg-[#deeefb]"
                      }`}
                    >
                      {/* Unread left border accent */}
                      {!n.read && (
                        <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#08757c] rounded-r-full" />
                      )}

                      {/* Icon */}
                      <div
                        className={`flex-shrink-0 w-8 h-8 rounded-xl ${meta.bg} flex items-center justify-center`}
                      >
                        <span
                          className={`material-symbols-outlined text-[16px] ${meta.iconColor}`}
                        >
                          {meta.icon}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-1">
                          <p
                            className={`text-[12px] leading-tight ${
                              n.read
                                ? "font-semibold text-slate-700"
                                : "font-bold text-slate-800"
                            }`}
                          >
                            {n.title}
                          </p>
                          {!n.read && (
                            <span
                              className={`flex-shrink-0 w-2 h-2 rounded-full mt-1 ${meta.dot}`}
                            />
                          )}
                        </div>
                        <div className="relative group/msg">
                          <p
                            className={`text-[11px] mt-0.5 leading-snug line-clamp-2 ${
                              n.read ? "text-slate-500" : "text-slate-600"
                            }`}
                          >
                            {n.message}
                          </p>
                          <div
                            className="absolute left-0 top-full mt-1 hidden group-hover/msg:block
                  bg-slate-800 text-white text-[11px] rounded-lg px-3 py-2
                  max-w-[280px] whitespace-normal break-words z-[60] shadow-lg"
                          >
                            {n.message}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-[10px] text-slate-500">
                            {fmtTime(n.timestamp)}
                          </p>
                          {n.read ? (
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">
                              Read
                            </span>
                          ) : (
                            <span className="text-[9px] font-bold text-[#08757c] uppercase tracking-wide">
                              New
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
